/**
 * deposito.ts
 * locx-deposito-materiales A2a — GET /servicios/deposito/purchase-orders(/:poId),
 * POST /servicios/deposito/receptions, GET /servicios/deposito/warehouses.
 *
 * Mirrors servicios' IPurchaseOrderReceiptPort / IStockLookupPort DTOs
 * (domain/ports/) — this is the HTTP-boundary shape, validated on both
 * backend and frontend from the same Zod schema.
 *
 * Price wall (design D3): none of these schemas declare `unitPrice`,
 * `currency`, `total`, `quotationId`, `supplierId`, `requesterId`, or
 * `approver*` — the closed vocabulary below is the whole surface a
 * `locx.stock.recepcionar`-only user can ever receive.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// DepositoPoSearchQuerySchema — GET /servicios/deposito/purchase-orders
// ---------------------------------------------------------------------------

export const DepositoPoSearchQuerySchema = z.object({
  search: z.string().trim().min(1).max(200).optional(),
  status: z.string().optional(),
  /** 'true' = only POs with something still to receive (pendingLineCount > 0); totalCount counts only those. */
  pending: z.enum(['true']).optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(20),
});
export type DepositoPoSearchQuery = z.infer<typeof DepositoPoSearchQuerySchema>;

// ---------------------------------------------------------------------------
// Receivability — closed 5-state vocabulary (design D5).
// ---------------------------------------------------------------------------

export const DEPOSITO_PO_LINE_RECEIVABILITY = ['receivable', 'complete', 'not_catalogued', 'not_goods', 'annulled'] as const;
export const DepositoPoLineReceivabilitySchema = z.enum(DEPOSITO_PO_LINE_RECEIVABILITY);
export type DepositoPoLineReceivability = z.infer<typeof DepositoPoLineReceivabilitySchema>;

// ---------------------------------------------------------------------------
// DepositoPoLineSchema — mirrors ReceivablePoLineView.
// ---------------------------------------------------------------------------

export const DepositoPoLineSchema = z.object({
  caseItemId: z.string().uuid(),
  caseId: z.string().uuid(),
  poLineId: z.string().uuid(),
  lineNo: z.number().int(),
  description: z.string(),
  itemMasterId: z.string().uuid().nullable(),
  itemCode: z.string().nullable(),
  unit: z.string(),
  kind: z.enum(['bien', 'servicio']),
  qtyOrdered: z.string(),
  qtyReceived: z.string(),
  receivability: DepositoPoLineReceivabilitySchema,
});
export type DepositoPoLine = z.infer<typeof DepositoPoLineSchema>;

// ---------------------------------------------------------------------------
// DepositoPoSearchHitSchema / DepositoPoSearchResponseSchema
// ---------------------------------------------------------------------------

export const DepositoPoSearchHitSchema = z.object({
  poId: z.string().uuid(),
  code: z.string(),
  supplierName: z.string(),
  status: z.string(),
  statusLabel: z.string(),
  emittedAt: z.string(),
  receivableLineCount: z.number().int().nonnegative(),
  /**
   * Count of lines still needing SOME action before this PO is done for
   * Depósito purposes (receivable OR not_catalogued) — what the frontend's
   * "Recepción completa"/"Pendiente de recepción" badge derives from.
   * `receivableLineCount` alone is NOT safe for that: a not_catalogued line
   * has no itemMasterId to compare qty against, so it never counts there,
   * even though the PO isn't actually done (2026-09-04 bug).
   */
  pendingLineCount: z.number().int().nonnegative(),
});
export type DepositoPoSearchHit = z.infer<typeof DepositoPoSearchHitSchema>;

export const DepositoPoSearchResponseSchema = z.object({
  items: z.array(DepositoPoSearchHitSchema),
  totalCount: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  totalPages: z.number().int().nonnegative(),
});
export type DepositoPoSearchResponse = z.infer<typeof DepositoPoSearchResponseSchema>;

// ---------------------------------------------------------------------------
// DepositoPoDetailSchema — mirrors ReceivablePurchaseOrderView.
// ---------------------------------------------------------------------------

export const DepositoPoDetailSchema = z.object({
  poId: z.string().uuid(),
  code: z.string(),
  /**
   * requi-cotizacion-consolidada-por-rubro (Slice B) — null for a
   * consolidated (round-owned) OC since migration 0203. Every line's own
   * `caseId` (DepositoPoLineSchema, always set) is what reception actually
   * acts against.
   */
  caseId: z.string().uuid().nullable(),
  supplierName: z.string(),
  status: z.string(),
  statusLabel: z.string(),
  emittedAt: z.string(),
  receiptNumber: z.string().nullable(),
  lines: z.array(DepositoPoLineSchema),
});
export type DepositoPoDetail = z.infer<typeof DepositoPoDetailSchema>;

// ---------------------------------------------------------------------------
// DepositoReceptionCreateSchema / DepositoReceptionResponseSchema —
// POST /servicios/deposito/receptions
// ---------------------------------------------------------------------------

export const DepositoReceptionCreateSchema = z
  .object({
    poId: z.string().uuid(),
    caseId: z.string().uuid(),
    caseItemId: z.string().uuid(),
    /** Delta for THIS delivery, never the accumulated total (design D2). */
    qty: z.coerce.number().positive(),
    warehouseId: z.string().uuid(),
  })
  .strict();
export type DepositoReceptionCreate = z.infer<typeof DepositoReceptionCreateSchema>;

export const DepositoReceptionResponseSchema = z.object({
  caseItemId: z.string().uuid(),
  status: z.string(),
  qtyReceivedAccumulated: z.string(),
  stockApplied: z.boolean(),
});
export type DepositoReceptionResponse = z.infer<typeof DepositoReceptionResponseSchema>;

// ---------------------------------------------------------------------------
// Batch reception — the whole delivery note in one save (odd/tasks/compras-deposito.md S3).
// All-or-nothing: every line is received in ONE transaction; if any line fails
// nothing is recorded and the error names the failing caseItemId.
// ---------------------------------------------------------------------------

export const DepositoReceptionBatchLineSchema = z
  .object({
    caseId: z.string().uuid(),
    caseItemId: z.string().uuid(),
    /** Delta for THIS delivery, never the accumulated total. */
    qty: z.coerce.number().positive(),
  })
  .strict();
export type DepositoReceptionBatchLine = z.infer<typeof DepositoReceptionBatchLineSchema>;

export const DepositoReceptionBatchCreateSchema = z
  .object({
    poId: z.string().uuid(),
    warehouseId: z.string().uuid(),
    /** Supplier's delivery note (remito) number; recorded on every stock movement's note. */
    deliveryNoteNumber: z.string().trim().min(1).max(60).optional(),
    lines: z.array(DepositoReceptionBatchLineSchema).min(1).max(200),
  })
  .strict()
  .refine((b) => new Set(b.lines.map((l) => l.caseItemId)).size === b.lines.length, {
    message: 'Each line may appear only once.',
    path: ['lines'],
  });
export type DepositoReceptionBatchCreate = z.infer<typeof DepositoReceptionBatchCreateSchema>;

export const DepositoReceptionBatchResponseSchema = z.object({
  poId: z.string().uuid(),
  lines: z.array(DepositoReceptionResponseSchema),
});
export type DepositoReceptionBatchResponse = z.infer<typeof DepositoReceptionBatchResponseSchema>;

// ---------------------------------------------------------------------------
// DepositoWarehouseOptionSchema — GET /servicios/deposito/warehouses (design D11)
// ---------------------------------------------------------------------------

export const DepositoWarehouseOptionSchema = z.object({
  warehouseId: z.string().uuid(),
  name: z.string(),
  code: z.string().nullable(),
});
export type DepositoWarehouseOption = z.infer<typeof DepositoWarehouseOptionSchema>;

// ---------------------------------------------------------------------------
// DepositoServiceOrderOptionSchema — GET /servicios/deposito/service-orders
// (design D11 — the narrow OT picker; NOT GET /servicios/orders).
// ---------------------------------------------------------------------------

export const DepositoServiceOrderOptionSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  title: z.string(),
  state: z.string(),
});
export type DepositoServiceOrderOption = z.infer<typeof DepositoServiceOrderOptionSchema>;

// ---------------------------------------------------------------------------
// DepositoDispatchCreateSchema / DepositoDispatchSchema — Block B (locx-
// deposito-materiales B1c). Independent of contracts-compras'
// StockDispatchCreateSchema (no cross-package import between contracts
// packages — same posture as DepositoPoLineSchema mirroring
// ReceivablePoLineView above).
// ---------------------------------------------------------------------------

export const DepositoDispatchLineSchema = z.object({
  itemMasterId: z.string().uuid(),
  qty: z.coerce.number().positive(),
});
export type DepositoDispatchLine = z.infer<typeof DepositoDispatchLineSchema>;

export const DepositoDispatchCreateSchema = z
  .object({
    warehouseId: z.string().uuid(),
    /** Never in the future — re-asserted by CreateLocxDispatchUseCase (D10). */
    dispatchedAt: z.string().refine((v) => !Number.isNaN(Date.parse(v)) && new Date(v).getTime() <= Date.now(), {
      message: 'dispatchedAt must be a valid date that is not in the future',
    }),
    deliveredToName: z.string().trim().min(1),
    destinationServiceOrderId: z.string().uuid().optional(),
    destinationNote: z.string().optional(),
    lines: z
      .array(DepositoDispatchLineSchema)
      .min(1)
      .superRefine((lines, ctx) => {
        const seen = new Set<string>();
        for (const [index, line] of lines.entries()) {
          if (seen.has(line.itemMasterId)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Duplicate itemMasterId in dispatch lines', path: [index, 'itemMasterId'] });
          }
          seen.add(line.itemMasterId);
        }
      }),
    fileIds: z.array(z.string().uuid()).optional(),
  })
  .strict();
export type DepositoDispatchCreate = z.infer<typeof DepositoDispatchCreateSchema>;

export const DepositoDispatchLineResponseSchema = z.object({
  lineId: z.string().uuid(),
  itemMasterId: z.string().uuid(),
  itemCode: z.string().nullable(),
  description: z.string(),
  unit: z.string(),
  qty: z.string(),
  stockMovementId: z.string().uuid(),
  balanceAfter: z.string(),
});
export type DepositoDispatchLineResponse = z.infer<typeof DepositoDispatchLineResponseSchema>;

export const DepositoDispatchAttachmentSchema = z.object({
  fileId: z.string().uuid(),
  fileName: z.string(),
  contentType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
});
export type DepositoDispatchAttachment = z.infer<typeof DepositoDispatchAttachmentSchema>;

export const DepositoDispatchSchema = z.object({
  dispatchId: z.string().uuid(),
  code: z.string(),
  warehouseId: z.string().uuid(),
  warehouseName: z.string(),
  dispatchedAt: z.string(),
  deliveredToName: z.string(),
  destinationServiceOrderId: z.string().uuid().nullable(),
  destinationNote: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
  lines: z.array(DepositoDispatchLineResponseSchema),
  attachments: z.array(DepositoDispatchAttachmentSchema),
});
export type DepositoDispatch = z.infer<typeof DepositoDispatchSchema>;

// ---------------------------------------------------------------------------
// Movements ledger — Phase 6 (PR C1, design D1) —
// GET /servicios/deposito/movements?warehouseId=&itemMasterId=&from=&to=&page=&pageSize=
// Mirrors servicios' IStockLookupPort StockLedger* DTOs (domain/ports/) —
// this is the HTTP-boundary shape. `destinationServiceOrderCode` is the ONE
// field this schema adds beyond StockLedgerRowView — servicios' own
// enrichment (GetLocxStockLedgerUseCase), never a bare id on the wire.
// ---------------------------------------------------------------------------

export const DepositoMovementsQuerySchema = z.object({
  warehouseId: z.string().uuid().optional(),
  itemMasterId: z.string().uuid().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(20),
});
export type DepositoMovementsQuery = z.infer<typeof DepositoMovementsQuerySchema>;

export const STOCK_LEDGER_SOURCE_KINDS = ['reception', 'dispatch', 'adjustment', 'count', 'other'] as const;
export const StockLedgerSourceKindSchema = z.enum(STOCK_LEDGER_SOURCE_KINDS);
export type StockLedgerSourceKind = z.infer<typeof StockLedgerSourceKindSchema>;

export const DepositoMovementSourceSchema = z.object({
  kind: StockLedgerSourceKindSchema,
  poId: z.string().uuid().nullable(),
  poCode: z.string().nullable(),
  caseId: z.string().uuid().nullable(),
  caseItemId: z.string().uuid().nullable(),
  dispatchId: z.string().uuid().nullable(),
  dispatchCode: z.string().nullable(),
  deliveredToName: z.string().nullable(),
  destinationServiceOrderId: z.string().uuid().nullable(),
  destinationNote: z.string().nullable(),
});
export type DepositoMovementSource = z.infer<typeof DepositoMovementSourceSchema>;

export const DepositoMovementRowSchema = z.object({
  movementId: z.string().uuid(),
  occurredAt: z.string(),
  warehouseId: z.string().uuid(),
  warehouseName: z.string(),
  itemMasterId: z.string().uuid(),
  code: z.string().nullable(),
  description: z.string(),
  unit: z.string(),
  type: z.string(),
  direction: z.enum(['in', 'out']),
  qty: z.string(),
  balanceAfter: z.string(),
  note: z.string().nullable(),
  actorId: z.string(),
  source: DepositoMovementSourceSchema,
  /** servicios' own enrichment (GetLocxStockLedgerUseCase) — never a bare id on the wire. */
  destinationServiceOrderCode: z.string().nullable(),
});
export type DepositoMovementRow = z.infer<typeof DepositoMovementRowSchema>;

export const DepositoMovementsResponseSchema = z.object({
  items: z.array(DepositoMovementRowSchema),
  totalCount: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  totalPages: z.number().int().nonnegative(),
});
export type DepositoMovementsResponse = z.infer<typeof DepositoMovementsResponseSchema>;
